import { useMemo } from 'react'

import { useDashboardPermissions } from '@components'

import { useGetCampaignQuery } from '../../../hooks/useGetCampaignQuery'
import type { Incentive, RecommendationId } from '../Recommendations.types'
import { getBonus } from '../modules/CampaignRecommendation/utils'

type CampaignIncentiveMapping = {
  recommendationId: RecommendationId
  // we need this because BE types are hard to type correctly
  incentive: {
    type: Incentive['type']
    isCompleted: boolean
    bonus: { value: number; currency: string }
  }
}

const INCENTIVE_TO_RECOMMENDATION_MAP: Record<Incentive['type'], RecommendationId | undefined> = {
  ORDER_SAMPLES: 'orderSamples',
  CREATE_PROMOTION: 'promotions',
  CREATE_N_OR_MORE_PRODUCTS: 'createProduct',
  VERIFY_DISCORD: 'joinDiscord',
  INSTALL_MOBILE_APP: 'creatorApp',
  INSTALL_N_OR_MORE_APPS: 'integrations',
  MAKE_FIRST_SALES: undefined,
  DOUBLE_SIGNUP_REFERRAL_BONUS: 'referral',
}

export const useCampaignStatus = () => {
  const { hasPermission } = useDashboardPermissions()
  const { campaignQuery } = useGetCampaignQuery(undefined, {
    enabled: hasPermission('home.campaign'),
  })

  const campaignIncentives = useMemo(() => {
    if (!campaignQuery.data) return []

    const incentives: CampaignIncentiveMapping[] = []

    campaignQuery.data.campaignIncentives.forEach((incentive) => {
      const recommendationId = INCENTIVE_TO_RECOMMENDATION_MAP[incentive.type]

      const bonus = getBonus(incentive.bonus)

      if (recommendationId && !incentive.isCompleted && bonus) {
        incentives.push({
          recommendationId,
          incentive: {
            ...incentive,
            bonus,
          },
        })
      }
    })

    return incentives
  }, [campaignQuery.data])

  return {
    isLoading: campaignQuery.isLoading,
    activeIncentives: campaignIncentives,
  }
}
